import { type Page, type Response, success, type UserInfo } from "@iflyrpa/share";
import type { SteelAction } from "./index";

/**
 * 微信公众号登录 RPA
 *
 * 流程：
 * 1. 打开微信公众号登录页
 * 2. 生成远程调试地址，等待用户扫码
 * 3. 监听 scanloginqrcode 接口判断扫码状态（0=未扫码, 4=已扫码未确认, 1=已确认）
 * 4. 扫码确认后监听页面跳转到 /wxamp/home，提取 token
 * 5. 从 __INITIAL_STATE__ 获取用户信息
 * 6. 收集 cookie 并返回结果
 */
export const rpaAction: SteelAction = async (task, _params) => {
    const reporter = task.reportService?.reportLoginStatus ?? (() => Promise.resolve());
    let page: Page;
    let isSelfClosing = false;
    let handleBrowserDisconnected: (() => void) | undefined;
    const cleanup = async () => {
        if (handleBrowserDisconnected) {
            task._steelBrowser?.off("disconnected", handleBrowserDisconnected);
        }
        if (page && !page.isClosed()) {
            await page.close().catch(() => {});
        }
    };

    // ========== 第一步：启动浏览器并打开登录页 ==========
    try {
        page = await task.createPage({
            url: "https://mp.weixin.qq.com/cgi-bin/loginpage",
        });
        if (!task._session?.id) throw new Error("Session ID 不存在");
        handleBrowserDisconnected = () => {
            if (task.debug && isSelfClosing) {
                task.logger.info("执行结束，关闭浏览器！");
            } else {
                task.logger.info("浏览器被手动释放或意外关闭！");
            }
        };
        task._steelBrowser?.on("disconnected", handleBrowserDisconnected);
    } catch (e) {
        return { code: 414, message: `浏览器启动失败: ${e}`, data: {} };
    }

    // ========== 第二步：生成远程调试地址 ==========
    const pageInfo = await task._client?.sessions.liveDetails(task._session.id);
    const pageId = pageInfo?.pages?.[0]?.id ?? "";

    const baseHost = task._session.websocketUrl?.replace(/^wss?:\/\//, "") ?? "";
    const connectAddress = new URLSearchParams({ baseHost, pageId }).toString();
    task.reportService?.settingSessionID?.(task._session?.id || "");

    await reporter(0, "浏览器启动成功，请使用微信扫码登录……", {
        stage: "connectAddress",
        connectAddress,
    });

    // ========== 第三步：监听扫码状态与登录结果 ==========
    let token: string | undefined;
    let userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };

    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            /** 清理所有监听器，防止重复触发 */
            const cleanup = (status?: unknown) => {
                if (finished) return;
                finished = true;
                page.off("response", handleAfterLogin);
                page.off("response", handleScanStatus);
                if (status !== undefined) reject(status);
            };

            /**
             * 登录确认后的处理：
             * 监听页面跳转到 /wxamp/ || /cgi-bin/home，从 URL 提取 token，
             * 从 __INITIAL_STATE__ 获取用户信息
             */
            const handleAfterLogin = async (resp: Response) => {
                try {
                    const url = resp.url();

                    if (url.includes("/wxamp/") || url.includes("/cgi-bin/home")) {
                        cleanup();
                        // 从 URL 中提取 token 参数
                        const tokenMatch = url.match(/token=(\d+)/);
                        if (tokenMatch) token = tokenMatch[1];

                        if (url.includes("/cgi-bin/home")) {
                            // 公众号后台首页使用 wx.commonData
                            try {
                                await page.waitForFunction(() => !!(window as any).wx?.commonData, { timeout: 5000 });
                                const commonData = await page.evaluate(() => (window as any).wx.commonData);
                                userInfo = {
                                    name: commonData?.data?.nick_name ?? "",
                                    uniqueId: commonData?.data?.user_name ?? "",
                                    avatar: commonData?.data?.head_img ?? "",
                                };
                            } catch (e) {
                                task.logger.warn("无法获取 commonData 用户信息:", {
                                    error: e instanceof Error ? e.message : String(e),
                                });
                            }
                        } else {
                            // 小程序主页使用 __INITIAL_STATE__
                            try {
                                await page.waitForFunction(() => !!(window as any).__INITIAL_STATE__?.userInfo, {
                                    timeout: 5000,
                                });
                                const initialState = await page.evaluate(() => (window as any).__INITIAL_STATE__);

                                userInfo = {
                                    name: initialState?.userInfo?.nickName ?? "",
                                    uniqueId: initialState?.userInfo?.userName ?? "",
                                    avatar: initialState?.userInfo?.iconUrl ?? "",
                                };
                            } catch (e) {
                                task.logger.warn("无法获取用户信息:", {
                                    error: e instanceof Error ? e.message : String(e),
                                });
                            }
                        }

                        resolve();
                    }
                } catch (e) {
                    cleanup(e);
                }
            };

            /**
             * 监听扫码状态接口 /cgi-bin/scanloginqrcode
             *
             * 状态码说明：
             *   0 = 用户未扫码
             *   4 = 用户已扫码未确认
             *   1 = 用户已确认登录
             */
            const handleScanStatus = async (response: Response) => {
                try {
                    const url = response.url();
                    if (
                        !url.includes("/cgi-bin/scanloginqrcode?action=ask") &&
                        !url.includes("/cgi-bin/loginqrcode?action=ask")
                    )
                        return;

                    let json: Record<string, unknown>;
                    try {
                        const text = await response.text();
                        if (!text) {
                            task.logger.warn("扫码状态接口返回空响应", { url });
                            return;
                        }
                        json = JSON.parse(text);
                    } catch (e) {
                        task.logger.warn("扫码状态接口响应解析失败", { url, error: e });
                        return;
                    }

                    // 已确认登录，切换监听：从扫码状态 → 登录结果
                    if (json.status === 1) {
                        page.off("response", handleScanStatus);
                        page.on("response", handleAfterLogin);
                        await reporter(0, "扫码成功!", { stage: "scanQrcode", connectAddress });
                    }
                } catch (e) {
                    cleanup(e);
                }
            };

            // 提前注册监听，防止漏掉响应
            page.on("response", handleScanStatus);
        });
    } catch (e) {
        // 扫码状态码 → 用户友好提示的映射
        const errMap: Record<string, string> = {
            "0": "扫码超时，请重试！",
            "4": "已扫码但未确认，请重试！",
        };
        isSelfClosing = true;

        const errorMessage =
            typeof e === "string" && errMap[e] ? errMap[e] : e instanceof Error ? e.message : String(e);

        await cleanup();
        return { code: 500, message: errorMessage, data: { stage: "scanQrcode", connectAddress } };
    }

    // ========== 第四步：收集 cookie 并返回结果 ==========
    const cookie = JSON.stringify(await task._steelBrowserContext?.cookies());
    isSelfClosing = true;
    await cleanup();

    return success({ connectAddress, token, cookie, userInfo }, "登录成功！");
};
